import { atom } from 'nanostores';
import { toast } from 'react-toastify';

import {
  cancelSubscriptionPricingAction,
  createTeamPricingAction,
  forbiddenPricingAction,
  handleManageCurrentOrgPlan,
  redirectToTeamOnboarding,
  redirectToTeamsMemberSelectPage,
  renewSubscriptionPricingAction,
} from './helpers';
import { PlansStore } from './plans';
import type { PricingCardAction } from './types';
import { openWithStackblitzAuth } from '~/lib/.client/openWithStackblitzAuth';
import type { PlanLevel } from '~/lib/.client/plans';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import {
  cancelTeamSubscription,
  cancelTeamSubscriptionDowngrade,
  cancelTeamSubscriptionUpdate,
  renewTeamSubscription,
} from '~/lib/membership/membershipApi';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent, CreateTeamTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import type { SubscriptionInterval } from '~/types/subscription';
import { hmrPropertyFactory } from '~/utils/hmr';
import { teamsPricingList, type Pricing } from '~/utils/pricing';

const hmr = hmrPropertyFactory('TeamPricingPlansStore');

class TeamPricingPlansStore extends PlansStore {
  @hmr()
  selectedOrg = atom<OrganizationData | null>(null);

  @hmr()
  actions = atom<PricingCardAction[]>([]);

  constructor() {
    super({
      defaultPricing: teamsPricingList[0],
      pricingList: teamsPricingList,
      pricingType: 'teams',
    });
  }

  initActiveState(organization: OrganizationData) {
    this.selectedOrg.set(organization);

    super._initActiveState({
      tier: organization.billing?.tier ?? null,
      nextTier: organization.billing?.nextTier,
      nextInterval: organization.billing?.nextInterval,
      subscription: organization.billing?.subscription,
    });
  }

  initInactiveState() {
    // inactive state is when we select a personal/create-team workspace

    this.displayMode = 'inactive';

    this.selectedOrg.set(null);
    this.membership.set(null);
    this.selectedPricing.set(this.defaultPricing);

    this.messages.set([]);

    this.actions.set([
      createTeamPricingAction(() =>
        this.startCreateTeamFlow(this.selectedPricing.get().level, pricingStore.selectedInterval.get()),
      ),
    ]);
  }

  getActions(pricing: Pricing, interval: SubscriptionInterval) {
    const organization = this.selectedOrg.get();

    if (organization?.role !== 'admin') {
      return [forbiddenPricingAction()];
    }

    const actions = super.getActions(pricing, interval);

    const { isActive, isCurrentPlanCanceled, isDowngraded } = this.getDisplayState(pricing, interval);

    if (!!organization && isActive && !isCurrentPlanCanceled) {
      actions.push(
        cancelSubscriptionPricingAction({
          plan: 'teams',
          isDowngraded,
          organization,
          onCancel: () => teamPricingPlansStore.cancelSubscription(),
        }),
      );
    }

    if (isActive && isCurrentPlanCanceled) {
      actions.push(renewSubscriptionPricingAction(() => this.renewSubscription()));
    }

    return actions;
  }

  async manageSubscription() {
    const organization = this.selectedOrg.get();

    if (!organization) {
      toast.error('Could not find the organization to manage subscription');
      return;
    }

    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.ManageSubscriptionClicked,
        properties: {
          // user context (who triggered the action)
          user_email: user?.email,
          user_role: organization.role,
          user_id: user?.id,

          // plan context
          plan_type: 'teams',
          organization_id: organization.id,
          organization_slug: organization.slug,
          current_tier: organization?.billing?.tier,
          billing_interval: organization?.billing?.subscription?.interval,
          subscription_status: organization?.billing?.subscription?.status,
          member_count: organization?.billing?.subscription?.quantity || 1,

          // action context
          location: 'team_pricing_store',
        },
      },
    });

    this._startRequestForPricing('manage-subscription');

    try {
      await handleManageCurrentOrgPlan(organization);
      this._endRequestForPricing();
    } catch (error) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.BillingPortalRedirectFailed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'teams',
            organization_id: organization.id,
            error_message: String(error),
          },
        },
      });
      throw error;
    }
  }

  async changePlan(pricing: Pricing, updateType: 'upgrade' | 'downgrade' | 'change_interval') {
    const organization = this.selectedOrg.get();

    const user = userStore.getUser();
    const interval = pricingStore.selectedInterval.get();
    const hasSubscription = (organization?.billing?.tier ?? 0) > 0;
    const isCanceled = organization?.billing?.subscription?.status === 'canceled';

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.PlanChangeStarted,
        properties: {
          // user context (who triggered the action)
          user_email: user?.email,
          user_role: organization?.role,
          user_id: user?.id,

          // plan context
          plan_type: 'teams',
          update_type: updateType,
          organization_id: organization?.id,
          organization_slug: organization?.slug,
          current_tier: organization?.billing?.tier,
          target_tier: pricing.tier,
          current_interval: organization?.billing?.subscription?.interval,
          target_interval: interval,
          pricing_level: pricing.level,
          member_count: organization?.billing?.subscription?.quantity || 1,
          has_subscription: hasSubscription,
          is_canceled: isCanceled,

          // action context
          location: 'team_change_plan',
        },
      },
    });

    if (!organization) {
      this.startCreateTeamFlow(pricing.level, interval);
      this._endRequestForPricing();

      return;
    }

    if (!hasSubscription) {
      redirectToTeamsMemberSelectPage(organization, pricing.level, interval);
      this._endRequestForPricing();

      return;
    }

    if (hasSubscription && isCanceled && user?.featureFlags.teamManagement) {
      redirectToTeamOnboarding({ slug: organization.slug, level: pricing.level, interval });
      this._endRequestForPricing();

      return;
    } else if (hasSubscription) {
      pricingStore.updatePlanScreenData.set({
        updateType,
        organization,
        pricing,
        interval,
      });

      return;
    }
  }

  async cancelSubscription() {
    const organization = this.selectedOrg.get();

    if (!organization) {
      toast.error('Could not find the organization to cancel subscription');
      return;
    }

    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.CancelSubscriptionClicked,
        properties: {
          // user context
          user_email: user?.email,
          user_id: user?.id,
          user_role: organization.role,

          // plan context
          plan_type: 'teams',
          organization_id: organization.id,
          current_tier: organization?.billing?.tier,
          billing_interval: organization?.billing?.subscription?.interval,
          subscription_status: organization?.billing?.subscription?.status,
          member_count: organization?.billing?.subscription?.quantity || 1,

          // action context
          location: 'team_cancel_subscription',
        },
      },
    });

    this._startRequestForPricing('cancel-subscription');

    try {
      await cancelTeamSubscription(organization);

      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.SubscriptionCancellationConfirmed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'teams',
            organization_id: organization.id,
            current_tier: organization?.billing?.tier,
          },
        },
      });
    } catch (error) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.PlanChangeFailed,
          properties: {
            user_email: user?.email,
            user_id: user?.id,
            plan_type: 'teams',
            organization_id: organization.id,
            action_type: 'cancel_subscription',
            error_message: String(error),
          },
        },
      });
      throw error;
    } finally {
      this._endRequestForPricing();
    }
  }

  async cancelSubscriptionUpdate(organization: OrganizationData) {
    this._startRequestForPricing('cancel-subscription-update');

    await cancelTeamSubscriptionUpdate(organization);

    this._endRequestForPricing();
  }

  async renewSubscription() {
    const organization = this.selectedOrg.get();

    if (!organization) {
      toast.error('Could not find the organization to renew subscription');
      return;
    }

    this._startRequestForPricing('renew-subscription');

    await renewTeamSubscription(organization);

    this._endRequestForPricing();
  }

  async cancelDowngrade(pricing: Pricing) {
    const organization = this.selectedOrg.get();

    if (!organization) {
      toast.error('Could not find the organization to cancel subscription downgrade');
      return;
    }

    this._startRequestForPricing('cancel-downgrade');

    await cancelTeamSubscriptionDowngrade(organization, pricing.level);

    this._endRequestForPricing();
  }

  startCreateTeamFlow(selectedLevel?: PlanLevel, selectedInterval?: SubscriptionInterval) {
    const user = userStore.getUser();
    const level = selectedLevel ?? 'teams_1';
    const interval = selectedInterval ?? 'yearly';

    if (user?.featureFlags.teamManagement) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.CreateTeamModalOpened,
          properties: {
            trigger: CreateTeamTrigger.PlanCard,
            level,
            interval,
          },
        },
      });
      teamStore.createTeamSlugData.set({ level, interval });
    } else {
      openWithStackblitzAuth(
        `/?createTeam=true&level=${level}&interval=${interval}&return_to=${window.origin}/?showPricing=true`,
      );
    }
  }
}

export const teamPricingPlansStore = new TeamPricingPlansStore();

pricingStore.selectedInterval.subscribe((interval) => {
  teamPricingPlansStore.onIntervalChange(interval);
});

userStore.getActiveOrganization().subscribe((organization) => {
  if (!organization?.billing) {
    return;
  }

  const tier = organization.billing.tier ?? null;
  const nextTier = organization.billing.nextTier ?? null;

  teamPricingPlansStore.membership.set({
    tier,
    nextTier,
    nextInterval: organization.billing.nextInterval,
    subscription: organization.billing.subscription,
  });

  if (tier) {
    teamPricingPlansStore.activePricing.set(teamsPricingList.find((pricing) => pricing.tier === tier) ?? null);
  }
});

if (import.meta.env.DEV) {
  Object.assign(globalThis, { teamPricingPlansStore });
}
